import {
  checklistSectionSchema,
  streamerChecklistSchema,
  type ChecklistEntry,
  type StreamerChecklist,
} from '@growup/streaming-shared';
import { Button, cx, Field, Input, Notice, StatusBadge } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { formatDate } from '../format';
import { MARKED_BY_LABEL, SECTION_LABEL } from './labels';

/** A cor de quem marca, igual a do painel do streamer. */
const WHO_LOOK: Record<ChecklistEntry['markedBy'], string> = {
  streamer: 'bg-gu-accent text-gu-on-accent',
  padrinho: 'bg-gu-info/15 text-gu-info',
  auto: 'bg-gu-success/15 text-gu-success',
};

type Editing = { itemId: string; mode: 'done' | 'na' } | null;

/**
 * A checklist dos 30 dias de um streamer (GS4, parte B), for the padrinho
 * (`base` = their afilhado) or the Stream Staff (`staff`, can also reopen).
 * Ticks the padrinho's items, marks any item as not applicable with a reason,
 * extends by 15 or 30 days and closes when every required item is settled.
 */
export function ChecklistPanel({ base, staff = false }: { base: string; staff?: boolean }) {
  const [data, setData] = useState<StreamerChecklist | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Os passos ja completos ao abrir vem fechados; os outros abertos (e um passo
  // que se completa agora fica aberto). Um clique troca.
  const [toggled, setToggled] = useState<Set<string>>(new Set());
  const [doneAtLoad, setDoneAtLoad] = useState<Set<string>>(new Set());

  useEffect(() => {
    getJson(base, streamerChecklistSchema)
      .then((d) => {
        setData(d);
        setDoneAtLoad(
          new Set(
            checklistSectionSchema.options.filter((section) => {
              const entries = d.entries.filter((e) => e.section === section);
              return entries.length > 0 && entries.every((e) => e.done || e.notApplicable);
            }),
          ),
        );
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [base]);

  const run = useCallback(async (method: string, path: string, body?: unknown) => {
    setError(null);
    try {
      setData(streamerChecklistSchema.parse(await send(method, path, body)));
      setEditing(null);
      setNote('');
    } catch (e) {
      setError(messageOf(e));
    }
  }, []);

  if (data === null) return error ? <Notice tone="danger">{error}</Notice> : null;
  const closed = data.closedAt !== null;
  const settled = data.requiredDone === data.requiredTotal;

  return (
    <div className="flex flex-col gap-3" aria-label="Checklist dos 30 dias">
      <div className="flex flex-col gap-1">
        <p className="font-display text-headline">
          {data.done} de {data.total} feitos
        </p>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={data.total}
          aria-valuenow={data.done}
          aria-label={`${String(data.done)} de ${String(data.total)}`}
          className="h-2 w-full overflow-hidden rounded-full bg-gu-sunken"
        >
          <div
            className="h-full bg-gu-accent"
            style={{
              width: `${String(data.total > 0 ? Math.round((data.done / data.total) * 100) : 0)}%`,
            }}
          />
        </div>
      </div>
      <p className="text-gu-ink-muted">
        {closed
          ? `Fechada a ${formatDate(data.closedAt!)} por ${data.closedBy ?? '?'}.`
          : `Obrigatórios: ${String(data.requiredDone)} de ${String(data.requiredTotal)} · conversa dos 30 dias até ${formatDate(data.dueOn)}${data.extraDays > 0 ? ` (prolongada ${String(data.extraDays)} dias)` : ''}`}
      </p>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {checklistSectionSchema.options
        .filter((section) => data.entries.some((e) => e.section === section))
        .map((section, index) => {
          const entries = data.entries.filter((e) => e.section === section);
          const settledCount = entries.filter((e) => e.done || e.notApplicable).length;
          const complete = settledCount === entries.length;
          const open = doneAtLoad.has(section) === toggled.has(section);
          return (
            <section key={section} className="flex flex-col gap-1 border-t border-gu-line pt-3">
              <h3>
                <button
                  type="button"
                  aria-expanded={open}
                  className="flex w-full items-center gap-3 text-left"
                  onClick={() =>
                    setToggled((prev) => {
                      const next = new Set(prev);
                      if (next.has(section)) next.delete(section);
                      else next.add(section);
                      return next;
                    })
                  }
                >
                  <span
                    aria-hidden="true"
                    className={cx(
                      'grid size-7 shrink-0 place-items-center rounded-full border-2 text-caption font-bold',
                      complete
                        ? 'border-gu-success bg-gu-success text-gu-ground'
                        : 'border-gu-accent text-gu-ink',
                    )}
                  >
                    {complete ? '✓' : index + 1}
                  </span>
                  <span className="flex-1 font-sub text-label font-bold uppercase">
                    {SECTION_LABEL[section]}
                  </span>
                  <span className="text-caption text-gu-ink-muted">
                    {settledCount} de {entries.length} {open ? '▾' : '▸'}
                  </span>
                </button>
              </h3>
              {open ? (
                <ul className="flex flex-col divide-y divide-gu-line sm:pl-10">
                  {entries.map((e) => (
                    <li
                      key={e.itemId}
                      className="flex flex-col gap-2 py-2"
                      aria-label={`Checklist: ${e.title}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <span className="flex flex-col">
                          <span className="flex flex-wrap items-center gap-2">
                            <span>
                              {e.title}
                              {e.required ? '' : ' (opcional)'}
                            </span>
                            <span
                              className={cx(
                                'rounded-full px-2 py-0.5 font-sub text-caption font-bold uppercase',
                                WHO_LOOK[e.markedBy],
                              )}
                            >
                              {MARKED_BY_LABEL[e.markedBy]}
                            </span>
                          </span>
                          {describe(e) ? (
                            <span className="text-caption text-gu-ink-muted">{describe(e)}</span>
                          ) : null}
                        </span>
                        <span className="flex flex-wrap items-center gap-2">
                          <StatusBadge
                            tone={e.notApplicable ? 'neutral' : e.done ? 'success' : 'warning'}
                          >
                            {e.notApplicable ? 'Não se aplica' : e.done ? 'Feito' : 'Por fazer'}
                          </StatusBadge>
                          {!closed && !e.done && e.markedBy === 'padrinho' ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setEditing({ itemId: e.itemId, mode: 'done' })}
                            >
                              Marcar
                            </Button>
                          ) : null}
                          {!closed && !e.done ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditing({ itemId: e.itemId, mode: 'na' })}
                            >
                              Não se aplica
                            </Button>
                          ) : null}
                          {!closed && e.doneBy !== null ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => void run('DELETE', `${base}/${e.itemId}`)}
                            >
                              Desfazer
                            </Button>
                          ) : null}
                        </span>
                      </div>
                      {editing?.itemId === e.itemId ? (
                        <div className="flex flex-wrap items-end gap-2">
                          <Field
                            label={editing.mode === 'na' ? 'Porque não se aplica' : 'Nota'}
                            optional={editing.mode === 'done'}
                            className="min-w-64 flex-1"
                          >
                            <Input
                              value={note}
                              maxLength={500}
                              onChange={(ev) => setNote(ev.target.value)}
                            />
                          </Field>
                          <Button
                            size="sm"
                            className="mb-1.5"
                            disabled={editing.mode === 'na' && note.trim().length < 3}
                            onClick={() =>
                              void run('PUT', `${base}/${e.itemId}`, {
                                notApplicable: editing.mode === 'na',
                                note,
                              })
                            }
                          >
                            Confirmar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="mb-1.5"
                            onClick={() => setEditing(null)}
                          >
                            Cancelar
                          </Button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          );
        })}
      <div className="flex flex-wrap gap-2">
        {!closed ? (
          <>
            <Button
              size="sm"
              disabled={!settled}
              title={settled ? undefined : 'Faltam obrigatórios'}
              onClick={() => void run('POST', `${base}/close`)}
            >
              Fechar a checklist
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void run('POST', `${base}/extend`, { days: 15 })}
            >
              Prolongar 15 dias
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void run('POST', `${base}/extend`, { days: 30 })}
            >
              Prolongar 30 dias
            </Button>
          </>
        ) : staff ? (
          <Button size="sm" variant="secondary" onClick={() => void run('POST', `${base}/reopen`)}>
            Reabrir
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function describe(e: ChecklistEntry): string {
  const parts: string[] = [];
  if (e.progress) parts.push(e.progress);
  if (e.doneBy && e.doneAt) parts.push(`${e.doneBy} a ${formatDate(e.doneAt)}`);
  if (e.note) parts.push(e.note);
  return parts.join(' · ');
}
